/* eslint-disable @typescript-eslint/camelcase */
import Apis from 'apis';
import { objToQueryString } from 'utils/helpers';

export const api = Apis({});

interface QueryProps {
  limit?: number | string;
  offset?: number | string;
  search?: string;
  order?: string;
}

export default {
  getAllEnvironments: (query: QueryProps) =>
    `/environment/?${objToQueryString(query)}`,
  getAllEnvironmentsOperator: (query: QueryProps) =>
    `/environment/summary/?${objToQueryString(query)}`,
  removeMultipleModels: (environmentUuid: string) =>
    `/environment/${environmentUuid}/model/remove-models/`,
  editEnvironment: (environmentUuid: string) =>
    `/environment/${environmentUuid}/`,
  getEnvironment: (environmentUuid: string) =>
    `/environment/${environmentUuid}/`,
  getHrlsEnvironmentDashboard: (
    environmentUuid: string,
    intervalTime: string
  ) =>
    `/environment/${environmentUuid}/dashboard/chart/session/pages/?period=${intervalTime}`,
  getAllLogsImageEnvironments: (
    environmentUuid: string,
    instanceId: string,
    query: QueryProps
  ) =>
    `/environment/${environmentUuid}/image/publication/${instanceId}/logs/?${objToQueryString(
      query
    )}`,
  getSessionsData: (
    environmentUuid: string,
    beginPeriod: string,
    endPeriod: string
  ) =>
    `/environment/${environmentUuid}/dashboard/chart/session/data/?begin_period=${beginPeriod}&end_period=${endPeriod}`,
  getInstanceData: (
    environmentUuid: string,
    beginPeriod: string,
    endPeriod: string
  ) =>
    `/environment/${environmentUuid}/dashboard/chart/instances/data/?begin_period=${beginPeriod}&end_period=${endPeriod}`,

  toggleBlockInstances: (
    environmentUuid: string,
    action: 'block' | 'unblock'
  ) => `/environment/${environmentUuid}/scale-instance/${action}/`,

  newEnvironment: () => `/environment/`,

  getEnvironmentBehavior: (
    environmentUuid: string,
    period: 'week' | 'saturday' | 'sunday'
  ) => `/environment/${environmentUuid}/behavior/${period}/`,
  editEnvironmentBehavior: (
    environmentUuid: string,
    period: 'week' | 'saturday' | 'sunday'
  ) => `/environment/${environmentUuid}/behavior/${period}/`,

  editEnvironmentBehaviorMode: (environmentUuid: string) =>
    `/environment/${environmentUuid}/`,

  getMaintenanceDisplayWarning: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/display-warning/`,
  getMaintenanceSchedule: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/schedule/`,

  getMaintenanceImmediate: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/immediate/`,

  getMaintenanceRemoveSession: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/auto-remove-user-session/`,

  editMaintenanceRemoveSession: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/auto-remove-user-session/`,
  editMaintenanceDisplayWarning: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/display-warning/`,
  editMaintenanceSchedule: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/schedule/`,

  editMaintenanceImmediate: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/immediate/`,

  getAllModels: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/model/?${objToQueryString(query)}`,
  registerModel: (environmentUuid: string, pathUrl: string) =>
    `/environment/${environmentUuid}/model/${pathUrl}`,
  editModel: (environmentUuid: string, pathUrl: string) =>
    `/environment/${environmentUuid}/model/${pathUrl}`,

  terminateInstance: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/scale-instance/${instanceId}/terminate/`,
  rebootInstance: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/scale-instance/${instanceId}/reboot/`,

  removeEnvironment: (environmentUuid: string) =>
    `/environment/${environmentUuid}/`,
  removeInstance: (environmentUuid: string) =>
    `/environment/${environmentUuid}/scale-instance/unregister/`,
  registerNewInstance: (environmentUuid: string) =>
    `/environment/${environmentUuid}/scale-instance/register/`,

  activateInstance: (environmentUuid: string) =>
    `/environment/${environmentUuid}/scale-instance/activate/`,

  removeSessionsInstance: () => `/session/remove-sessions/`,
  getSessionsInInstance: (
    environmentUuid: string,
    instanceUuid: string,
    query: QueryProps
  ) =>
    `/session/?environment_uuid=${environmentUuid}&instance_uuid=${instanceUuid}&${objToQueryString(
      query
    )}`,
  getInstanceObservability: (instanceUuid: string) =>
    `/instance-observability/${instanceUuid}/`,
  unlinkInstance: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/scale-instance/${instanceId}/unregister/`,
  getScaleInstances: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/scale-instance/?${objToQueryString(
      query
    )}`,
  getSecurity: (environmentUuid: string) =>
    `/environment/${environmentUuid}/password-policy/`,
  editSecurity: (environmentUuid: string) =>
    `/environment/${environmentUuid}/password-policy/`,

  getAllServers: (
    environmentUuid: string,
    force_refresh: boolean,
    query?: QueryProps,
    type?: string
  ) =>
    `/environment/${environmentUuid}/server/?${
      type ? `server_type=${type}&` : ''
    }force_refresh=${force_refresh}&${objToQueryString(query)}`,
  attachServer: (environmentUuid: string) =>
    `/environment/${environmentUuid}/server/`,
  detachServer: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/server/${instanceId}/`,
  registerServerPreparation: (environmentUuid: string) =>
    `/environment/${environmentUuid}/server/preparation/`,
  registerServerSupport: (environmentUuid: string) =>
    `/environment/${environmentUuid}/server/support/`,
  terminateMultipleInstance: (environmentUuid: string) =>
    `/environment/${environmentUuid}/scale-instance/terminate/`,

  publishNewImage: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/server/${instanceId}/publish-new-image/`,
  publicationKeepNewImage: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/image/publication/${instanceId}/keeping-user-connected/`,
  requestingUserNewImage: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/image/publication/${instanceId}/requesting-user-reconnection/`,
  standardNewImage: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/image/publication/${instanceId}/standard/`,
  toggleBlockUnblockMultipleInstances: (environmentUuid: string) =>
    `/environment/${environmentUuid}/scale-instance/toggle-blocked/`,

  startServer: (environmentUuid: string, serverId: string) =>
    `/environment/${environmentUuid}/server/${serverId}/start/`,
  stopServer: (environmentUuid: string, serverId: string) =>
    `/environment/${environmentUuid}/server/${serverId}/stop/`,
  restartServer: (environmentUuid: string, serverId: string) =>
    `/environment/${environmentUuid}/server/${serverId}/restart/`,

  duplicateModel: (environmentUuid: string, modelUuid: string) =>
    `/environment/${environmentUuid}/model/${modelUuid}/clone/`,

  stopTime: (environmentUuid: string, serverId: string) =>
    `/environment/${environmentUuid}/server/${serverId}/set-stop-time/`,
  getStatusPublication: (environmentUuid: string) =>
    `/environment/${environmentUuid}/image/publication/status/`,
  providerResources: (environmentUuid?: string, customId?: string) => ({
    keypairs: `/environment/${environmentUuid}/provider-resources/keypairs/`,
    images: `/environment/${environmentUuid}/provider-resources/instance/image/`,
    securityGroups: `/environment/${environmentUuid}/provider-resources/security-groups/`,
    amiFlavors: `/environment/${environmentUuid}/provider-resources/flavors/aws/ami/`,
    bootVolumeFlavors: `/environment/${environmentUuid}/provider-resources/flavors/oci/boot-volume/${customId}/`,
    bootVolumeBackupFlavors: `/environment/${environmentUuid}/provider-resources/flavors/oci/boot-volume-backup/${customId}/`,
    customImageFlavors: `/environment/${environmentUuid}/provider-resources/flavors/oci/custom-image/${customId}/`,
    zones: `/environment/${environmentUuid}/provider-resources/zones/`,
    subnets: `/environment/${environmentUuid}/provider-resources/subnets/`,
    bootVolume: `/environment/${environmentUuid}/provider-resources/instance/boot-volume/`,
    bootVolumeBackup: `/environment/${environmentUuid}/provider-resources/instance/boot-volume-backup/`,
    listProvidersResources: `/provider-resources/`,
  }),

  connectAs: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/server/${instanceId}/connect-as/`,

  getAlarmsInstance: (environmentUuid: string, instanceUuid: string) =>
    `/environment/${environmentUuid}/scale-instance/${instanceUuid}/alarms/`,

  getDashboardSessionsGraph: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/dashboard/sessions-graph/?${objToQueryString(
      query
    )}`,
  getDashboardCostGraph: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/dashboard/cost-graph/?${objToQueryString(
      query
    )}`,
  getEnvironmentFlavorsCost: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/dashboard/flavors-cost/?${objToQueryString(
      query
    )}`,
  getAllApplicationsAutosky: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/apps/autosky/summary/?${objToQueryString(
      query
    )}`,
  getClientsApplicationsAutosky: (
    environmentUuid: string,
    query: QueryProps,
    name: string,
    state?: string
  ) =>
    `/environment/${environmentUuid}/apps/autosky/?${objToQueryString(
      query
    )}&name=${name}&state=${state}`,
  renameClientsApplicationsAutosky: (environmentUuid: string) =>
    `/environment/${environmentUuid}/apps/autosky/bulk-rename/`,
  removeClientsApplicationsAutosky: (environmentUuid: string) =>
    `/environment/${environmentUuid}/apps/autosky/bulk-remove/`,
  changeStateClientsApplicationsAutosky: (environmentUuid: string) =>
    `/environment/${environmentUuid}/apps/autosky/bulk-change-state/`,
  includeClientsApplicationsAutosky: (environmentUuid: string) =>
    `/environment/${environmentUuid}/apps/autosky/bulk-create/`,
  getClientsToIncludeApplicationsAutosky: (
    environmentUuid: string,
    query: QueryProps,
    name: string
  ) =>
    `/environment/${environmentUuid}/apps/autosky/clients-to-include-app/?${objToQueryString(
      query
    )}&name=${name}`,

  getAllApplicationsPlatform: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/apps/platform/summary/?${objToQueryString(
      query
    )}`,
  getClientsApplicationsPlatform: (
    environmentUuid: string,
    query: QueryProps,
    code: string,
    state?: string
  ) =>
    `/environment/${environmentUuid}/apps/platform/?${objToQueryString(
      query
    )}&code=${code}&state=${state}`,
  removeClientsApplicationsPlatform: (environmentUuid: string) =>
    `/environment/${environmentUuid}/apps/platform/bulk-remove/`,
  migrateEnvironment: (environmentUuid: string) =>
    `/environment/${environmentUuid}/migrate/move-environment-to-another-partner/`,
  getEnvironmentPerformanceCPU: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/performance/dashboard/cpu/?${objToQueryString(
      query
    )}`,

  getEnvironmentPerformanceMemory: (
    environmentUuid: string,
    query: QueryProps
  ) =>
    `/environment/${environmentUuid}/performance/dashboard/memory/?${objToQueryString(
      query
    )}`,
  getEnvironmentMonitoring: (environmentUuid: string) =>
    `/environment/${environmentUuid}/monitoring/settings/`,
  editEnvironmentMonitoring: (environmentUuid: string) =>
    `/environment/${environmentUuid}/monitoring/settings/`,
  getMonitoringHistory: (
    environmentUuid: string,
    query: QueryProps,
    start_date?: string,
    end_date?: string
  ) =>
    `/environment/${environmentUuid}/monitoring/history/?${objToQueryString(
      query
    )}&end-date=${end_date}&start-date=${start_date}`,
  getEnvironmentPerformanceOperationalTime: (
    environmentUuid: string,
    query: QueryProps
  ) =>
    `/environment/${environmentUuid}/performance/dashboard/operational-time/?${objToQueryString(
      query
    )}`,
  getPublicationSchedule: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/image/publication/schedule/?${objToQueryString(
      query
    )}`,
  setPublicationSchedule: (environmentUuid: string) =>
    `/environment/${environmentUuid}/image/publication/schedule/`,
  cancelPublicationSchedule: (environmentUuid: string, scheduleUuid: string) =>
    `/environment/${environmentUuid}/image/publication/schedule/${scheduleUuid}/cancel/`,
  getBulkClients: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/clients/bulk-actions/?${objToQueryString(
      query
    )}`,
  editBulkClients: (environmentUuid: string) =>
    `/environment/${environmentUuid}/clients/bulk-actions/edit/`,
  acceptStatusPending: (environmentUuid: string) =>
    `/environment/${environmentUuid}/accept-pending/`,
  getBehaviorReport: (filters: QueryProps) =>
    `/environment/report/behavior/?${objToQueryString(filters)}`,
  getDNSReport: (filters: QueryProps) =>
    `/environment/report/dns/?${objToQueryString(filters)}`,
  getMaintenanceReport: (filters: QueryProps) =>
    `/environment/report/maintenance/?${objToQueryString(filters)}`,
  getProductReport: (filters: QueryProps) =>
    `/environment/report/product/?${objToQueryString(filters)}`,
  getProductTypeReport: (filters: QueryProps) =>
    `/environment/report/product-type/?${objToQueryString(filters)}`,
  getProviderReport: (filters: QueryProps) =>
    `/environment/report/provider/?${objToQueryString(filters)}`,
  getRegionReport: (filters: QueryProps) =>
    `/environment/report/region/?${objToQueryString(filters)}`,
  getStatusReport: (filters: QueryProps) =>
    `/environment/report/status/?${objToQueryString(filters)}`,
  getVirtualizerReport: (filters: QueryProps) =>
    `/environment/report/virtualizer/?${objToQueryString(filters)}`,
  getZeroTrustReport: (filters: QueryProps) =>
    `/environment/report/zero_trust/?${objToQueryString(filters)}`,
  getConnectionTypeEnvironmentReport: (filters: QueryProps) =>
    `/environment/report/connection-type/?${objToQueryString(filters)}`,
  getTimezones: () => `/environment/behavior/list-timezones/`,
  extendCompletionDate: (environmentUuid: string) =>
    `/environment/${environmentUuid}/extend-completion-date/`,
  getMaintenanceBlockInstances: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/auto-block-instances/`,
  getMaintenanceRemoveSessions: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/remove-sessions/`,
  editMaintenanceBlockInstances: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/auto-block-instances/`,
  editMaintenanceRemoveSessions: (environmentUuid: string) =>
    `environment/${environmentUuid}/maintenance/remove-sessions/`,
  exportEnvironmentSummary: (query: QueryProps) =>
    `/environment/summary/export/?${objToQueryString(query)}`,
  validateZeroTrust: (environmentUuid: string, instanceUuid: string) =>
    `/environment/${environmentUuid}/scale-instance/${instanceUuid}/zero-trust-validation/`,
  publishImage: (environmentUuid: string, instanceUuid: string) =>
    `/environment/${environmentUuid}/server/${instanceUuid}/publish-image/`,
  getPublishHistory: (environmentUuid: string) =>
    `/environment/${environmentUuid}/server/publications/`,
  exportHistoryMonitoringCSV: (environmentUuid: string) =>
    `/environment/${environmentUuid}/monitoring/history/export/`,
  blockServerActions: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/server/${instanceId}/block-actions/`,
  unblockServerActions: (environmentUuid: string, instanceId: string) =>
    `/environment/${environmentUuid}/server/${instanceId}/unblock-actions/`,
  getEnvironmentSessions: (environmentUuid: string, query: QueryProps) =>
    `/session/?environment_uuid=${environmentUuid}&${objToQueryString(query)}`,
  getLicenseGroups: (environmentUuid: string, query: QueryProps) =>
    `/environment/${environmentUuid}/license/group/?${objToQueryString(query)}`,
  createLicenseGroup: (environmentUuid: string) =>
    `/environment/${environmentUuid}/license/group/`,
  getLicenseGroupDetails: (environmentUuid: string, licenseGroupUuid: string) =>
    `/environment/${environmentUuid}/license/group/${licenseGroupUuid}/`,

  editLicenseGroup: (environmentUuid: string, licenseGroupUuid: string) =>
    `/environment/${environmentUuid}/license/group/${licenseGroupUuid}/`,
  deleteLicenseGroup: (environmentUuid: string, licenseGroupUuid: string) =>
    `/environment/${environmentUuid}/license/group/${licenseGroupUuid}/`,

  addClientsToLicenseGroup: (
    environmentUuid: string,
    licenseGroupUuid: string
  ) =>
    `/environment/${environmentUuid}/license/group/${licenseGroupUuid}/client/add/`,
  fullUpdateLicenseGroupClients: (
    environmentUuid: string,
    licenseGroupUuid: string
  ) =>
    `/environment/${environmentUuid}/license/group/${licenseGroupUuid}/client/full-update/`,
  getEnvironmentAdvisors: (environmentUuid: string, query: QueryProps) =>
    `/scaling-advisor/advisor/?environment_uuid=${environmentUuid}&${objToQueryString(
      query
    )}`,
  getAdminPin: (environmentUuid: string) =>
    `/environment/${environmentUuid}/environment-administrative-pin/administrative-pin/`,
  resetAdminPin: (environmentUuid: string) =>
    `/environment/${environmentUuid}/environment-administrative-pin/reset-pin/`,
  enableServerMonitoringManagement: (
    environmentUuid: string,
    serverId: string
  ) =>
    `/environment/${environmentUuid}/server/${serverId}/monitoring-management/enable/`,
  disableServerMonitoringManagement: (
    environmentUuid: string,
    serverId: string
  ) =>
    `/environment/${environmentUuid}/server/${serverId}/monitoring-management/disable/`,
  getServerMonitoringStatus: (environmentUuid: string, serverId) =>
    `/environment/${environmentUuid}/server/${serverId}/monitoring-status/`,
};
